/**
 * A confirmation dialog built from the stock Oxygen `Modal` (replacing the legacy
 * native `confirm()`). Used for consequential actions: changing a room to a
 * non-active status, and regenerating the setup code. The confirm button is
 * marked destructive for those flows.
 */

import type { ReactNode } from 'react';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '@8x8/oxygen-modal';
import Button from '@8x8/oxygen-button';

interface ConfirmModalProps {
    open: boolean;
    title: string;
    children: ReactNode;
    confirmLabel: string;
    destructive?: boolean;
    busy?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

export function ConfirmModal(props: ConfirmModalProps): JSX.Element | null {
    if (!props.open) {
        return null;
    }

    return (
        <Modal isVisible size="small" onClose={props.onClose} shouldCloseOnOverlayClick>
            <ModalHeader title={props.title} onClose={props.onClose} />
            <ModalContent>
                <div style={{ fontSize: 14, lineHeight: '21px', padding: '4px 0 8px' }}>{props.children}</div>
            </ModalContent>
            <ModalFooter>
                <Button variant="secondary" onClick={props.onClose}>Cancel</Button>
                <Button
                    variant="primary"
                    isDestructive={props.destructive}
                    isDisabled={props.busy}
                    onClick={props.onConfirm}
                >
                    {props.busy ? 'Working…' : props.confirmLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
